import * as React from 'react';
import { mergeStyles } from '@fluentui/react';
import { ThemeColorPalette } from '@fluidx/component-ux';
import { getSensitivityButton } from '@fluidx/information-protection-ux';
import { getPreviewButtonWithStrings } from '../previewLabel/getPreviewButtonWithStrings';
import { getLeapWithStrings } from '../leap/getLeapWithStrings';
import { Loading } from '../Loading';
import { useCanvasScroll } from './useCanvasScroll';
import { getShareButtonWithStrings } from './getShareButtonWithStrings';
import { getFileLocationButtonWithStrings } from './getFileLocationButtonWithStrings';
import { getOfficeNavButtonWithStrings } from './getOfficeNavButtonWithStrings';
import { useResolvedPromise } from '../../hooks';
const connectionStateConnected = 2;
// Styles for the bar that appears at the top of the page containing the share button, preview button, and coauthor gallery.
// The topBar has different styles before and after the first scroll event.
const topBarRestClassName = mergeStyles({
    height: 44,
    width: '100%',
    position: 'relative'
});
const topBarScrollClassName = mergeStyles(topBarRestClassName, {
    borderBottom: `1px solid ${ThemeColorPalette.Neutral.neutralQuaternaryAlt}`
});
const nearPeriphalControlsStyle = mergeStyles({
    position: 'absolute',
    left: 0
});
const farPeripheralControlsStyle = mergeStyles({
    position: 'absolute',
    right: 12,
    display: 'flex',
    alignItems: 'center'
});
/**
 * Custom React hook that returns true when the container has connected
 * @param container - the container that we wish to listen to events against
 * @returns - a boolean indicating if the container passed in has connected
 */ function useContainerFirstConnected(container) {
    const [hasConnected, setHasConnected] = React.useState(false);
    React.useEffect(()=>{
        setHasConnected(false);
        const onFirstConnected = ()=>{
            setHasConnected(true);
            container.off('connected', onFirstConnected);
        };
        container.on('connected', onFirstConnected);
        if (container.connectionState === connectionStateConnected) {
            onFirstConnected();
        }
        return ()=>{
            container.off('connected', onFirstConnected);
        };
    }, [
        container
    ]);
    return hasConnected;
}
/**
 * A toolbar that is rendered alongside a fluid container to provide the user with some common functionality associated with that container.
 */ export const ContainerToolbar = (props)=>{
    const { appColors , container , presenceColorProvider , logger , scrollingElementRef , tokenProvider , localization , onShareButtonClick , hideFileLocation , showOfficeNavButton , lastEditedTracker , presenceAwayStatusTrackerPromise , fileInfoPromise , shouldRenderPreviewButton , showInformationProtection , informationProtectionServicePromise , settingsProvider , stylingProvider  } = props;
    const isConsumer = tokenProvider.identityType === 'Consumer';
    const isUnauthenticatedGuest = tokenProvider.identityType === 'UnauthenticatedGuest';
    const Leap = React.useMemo(()=>/*#__PURE__*/ React.lazy(()=>getLeapWithStrings(localization)), [
        localization
    ]);
    const ShareButton = React.useMemo(()=>/*#__PURE__*/ React.lazy(()=>getShareButtonWithStrings(localization.locale)), [
        localization
    ]);
    const PreviewButton = React.useMemo(()=>/*#__PURE__*/ React.lazy(()=>getPreviewButtonWithStrings(localization.locale, tokenProvider.identityType || 'Enterprise')), [
        localization,
        tokenProvider
    ]);
    const FileLocationButton = React.useMemo(()=>/*#__PURE__*/ React.lazy(()=>getFileLocationButtonWithStrings(localization.locale)), [
        localization
    ]);
    const OfficeNavButton = React.useMemo(()=>/*#__PURE__*/ React.lazy(()=>getOfficeNavButtonWithStrings(localization.locale)), [
        localization
    ]);
    const SensitivityButton = React.useMemo(()=>/*#__PURE__*/ React.lazy(()=>getSensitivityButton(informationProtectionServicePromise)), [
        informationProtectionServicePromise
    ]);
    const topBarContainerRef = React.useRef(null);
    // The leap control only makes sense when we are connected, let's not render it until we have connected to avoid wasting CPU cycles on boot
    const showLeap = useContainerFirstConnected(container);
    const fileInfo = useResolvedPromise(fileInfoPromise);
    const shareDisabled = fileInfo === undefined;
    // Creates an effect where there is a drop shadow that only appears when the user has scrolled
    useCanvasScroll({
        scrollingElementRef,
        onScrollTop: ()=>{
            if (topBarContainerRef.current) {
                topBarContainerRef.current.className = topBarRestClassName;
            }
        },
        onScrollThresholdExceeded: ()=>{
            if (topBarContainerRef.current) {
                topBarContainerRef.current.className = topBarScrollClassName;
            }
        },
        scrollTopThreshold: 30
    });
    return /*#__PURE__*/ React.createElement("div", {
        className: topBarRestClassName,
        ref: topBarContainerRef
    }, shouldRenderPreviewButton && /*#__PURE__*/ React.createElement("div", {
        className: nearPeriphalControlsStyle
    }, /*#__PURE__*/ React.createElement(React.Suspense, {
        fallback: /*#__PURE__*/ React.createElement(Loading, null)
    }, /*#__PURE__*/ React.createElement(PreviewButton, {
        appColors: appColors
    }))), /*#__PURE__*/ React.createElement("div", {
        className: farPeripheralControlsStyle
    }, /*#__PURE__*/ React.createElement(React.Suspense, {
        fallback: /*#__PURE__*/ React.createElement(Loading, null)
    }, showLeap && /*#__PURE__*/ React.createElement(React.Suspense, {
        fallback: /*#__PURE__*/ React.createElement(Loading, null)
    }, /*#__PURE__*/ React.createElement(Leap, {
        container: container,
        presenceColorProvider: presenceColorProvider,
        pageLogger: logger,
        tokenProvider: tokenProvider,
        lastEditedTracker: lastEditedTracker,
        presenceAwayStatusTrackerPromise: presenceAwayStatusTrackerPromise,
        stylingProvider: stylingProvider
    })), showInformationProtection && settingsProvider && container.resolvedUrl && /*#__PURE__*/ React.createElement(SensitivityButton, {
        settingsProvider: settingsProvider,
        resolvedUrl: container.resolvedUrl,
        logger: logger,
        stylingProvider: stylingProvider
    }), !isUnauthenticatedGuest && onShareButtonClick && /*#__PURE__*/ React.createElement(ShareButton, {
        onClick: onShareButtonClick,
        disabled: shareDisabled
    }), !hideFileLocation && !isUnauthenticatedGuest && /*#__PURE__*/ React.createElement(FileLocationButton, {
        logger: logger,
        tokenProvider: tokenProvider,
        container: container
    }), showOfficeNavButton && !isConsumer && !isUnauthenticatedGuest && /*#__PURE__*/ React.createElement(OfficeNavButton, {
        logger: logger,
        fileInfoPromise: fileInfoPromise,
        tokenProvider: tokenProvider
    }))));
};
